import { useCallback, useRef } from 'react';
import { motion } from 'motion/react';
import { cn } from './cn';

export type TabsVariant = 'underline' | 'pill';

export interface TabItem {
  /** Unique key — matched against `value` and passed to `onChange`. */
  key: string;
  /** Visible label (i18n-agnostic — pass translated content). */
  label: React.ReactNode;
  /** Optional leading icon. */
  icon?: React.ReactNode;
  /** Optional count rendered as a small muted pill after the label. */
  count?: number;
}

export interface TabsProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Tab descriptors. */
  tabs: TabItem[];
  /** Currently selected tab key. */
  value: string;
  /** Called with the key of the newly selected tab. */
  onChange: (key: string) => void;
  /** Visual style. Default `underline`. */
  variant?: TabsVariant;
}

/**
 * Animated tabs — replaces MUI <Tabs />.
 * `underline`: flat row with a shared motion underline indicator.
 * `pill`: rounded-full segmented control with a shared motion thumb.
 * Keyboard: ArrowLeft / ArrowRight move between tabs (roving focus).
 */
export function Tabs({
  tabs,
  value,
  onChange,
  variant = 'underline',
  className,
  ...props
}: TabsProps) {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const moveFocus = useCallback(
    (currentIndex: number, dir: 1 | -1) => {
      const count = tabs.length;
      if (count === 0) return;
      const nextIndex = (currentIndex + dir + count) % count;
      const next = tabs[nextIndex];
      tabRefs.current[nextIndex]?.focus();
      onChange(next.key);
    },
    [tabs, onChange],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        moveFocus(index, 1);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        moveFocus(index, -1);
      } else if (e.key === 'Home') {
        e.preventDefault();
        tabRefs.current[0]?.focus();
        if (tabs[0]) onChange(tabs[0].key);
      } else if (e.key === 'End') {
        e.preventDefault();
        const last = tabs.length - 1;
        tabRefs.current[last]?.focus();
        if (tabs[last]) onChange(tabs[last].key);
      }
    },
    [moveFocus, onChange, tabs],
  );

  if (variant === 'pill') {
    return (
      <div
        role="tablist"
        className={cn(
          'inline-flex items-center gap-1 rounded-full bg-surface-2 p-1',
          className,
        )}
        {...props}
      >
        {tabs.map((tab, index) => {
          const selected = tab.key === value;
          return (
            <button
              key={tab.key}
              ref={(el) => {
                tabRefs.current[index] = el;
              }}
              type="button"
              role="tab"
              aria-selected={selected}
              tabIndex={selected ? 0 : -1}
              onClick={() => onChange(tab.key)}
              onKeyDown={(e) => onKeyDown(e, index)}
              className={cn(
                'relative inline-flex items-center justify-center gap-2 rounded-full px-4 py-2',
                'text-sm font-medium transition-colors duration-200 select-none whitespace-nowrap',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
                selected ? 'text-primary-fg' : 'text-muted hover:text-content',
              )}
            >
              {selected && (
                <motion.span
                  layoutId="tabs-pill"
                  className="absolute inset-0 rounded-full bg-surface shadow-card"
                  transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                />
              )}
              <span className="relative z-[1] inline-flex items-center gap-2">
                {tab.icon && (
                  <span className="inline-flex shrink-0" aria-hidden="true">
                    {tab.icon}
                  </span>
                )}
                {tab.label}
                {tab.count != null && <TabCount selected={selected}>{tab.count}</TabCount>}
              </span>
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div
      role="tablist"
      className={cn('flex items-center border-b border-line', className)}
      {...props}
    >
      {tabs.map((tab, index) => {
        const selected = tab.key === value;
        return (
          <button
            key={tab.key}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            type="button"
            role="tab"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(tab.key)}
            onKeyDown={(e) => onKeyDown(e, index)}
            className={cn(
              'relative inline-flex h-11 items-center justify-center gap-2 px-4',
              'text-sm font-medium transition-colors duration-200 select-none whitespace-nowrap',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-t-lg',
              selected ? 'text-primary' : 'text-muted hover:text-content',
            )}
          >
            {tab.icon && (
              <span className="inline-flex shrink-0" aria-hidden="true">
                {tab.icon}
              </span>
            )}
            {tab.label}
            {tab.count != null && <TabCount selected={selected}>{tab.count}</TabCount>}
            {selected && (
              <motion.span
                layoutId="tabs-underline"
                className="absolute inset-x-0 bottom-0 h-0.5 rounded-full bg-primary"
                transition={{ type: 'spring', stiffness: 380, damping: 32 }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

function TabCount({
  selected,
  children,
}: {
  selected: boolean;
  children: React.ReactNode;
}) {
  return (
    <span
      className={cn(
        'inline-flex min-w-5 items-center justify-center rounded-full px-1.5 py-0.5',
        'text-xs font-semibold leading-none transition-colors duration-200',
        selected ? 'bg-primary/15 text-primary-fg' : 'bg-surface-2 text-muted',
      )}
    >
      {children}
    </span>
  );
}
